<template>
  <div
    class="booking-add-ons-sidebar"
    role="dialog"
    aria-modal="true"
    aria-labelledby="booking-add-ons-sidebar-title"
  >
    <div class="booking-header-extension">
      <div
        class="booking-header-close"
        data-component="booking-header-close"
      >
        <button
          class="icon-close"
          :aria-label="tc('booking.close')"
          tabindex="0"
          @click="deactivate"
          @keypress.enter.prevent="deactivate"
          @keypress.space.prevent="deactivate"
        />
      </div>
      <div class="booking-header-extension-wrapper container">
        <div class="booking-header-extension-left">
          <div
            v-if="addOn.addOnName"
            id="booking-add-ons-sidebar-title"
            class="title"
          >
            {{ addOn.addOnName }}
          </div>
        </div>
      </div>
    </div>
    <div class="booking-add-ons-sidebar__wrapper">
      <div
        v-if="addOn.addOnImage"
        class="booking-add-ons-sidebar__image"
      >
        <picture
          v-lazyload="{
            src: addOn.addOnImage,
            set: [
              { width: '480', height: '240', breakpoint: 'xs' },
              { width: '800', height: '360', breakpoint: 'md' }
            ],
            fit: 'crop',
            align: 'center'
          }"
          class="image-wrapper"
        >
          <img
            class="image"
            src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
            :alt="addOn.addOnName"
          >
        </picture>
      </div>
      <div class="booking-add-ons-sidebar__content">
        <div
          v-if="addOnPrice"
          class="booking-add-ons-sidebar__eyebrow"
        >
          {{ priceAndTypeLabel }}
        </div>
        <div class="booking-add-ons-sidebar__header">
          {{ addOn.addOnName }}
        </div>
        <div
          class="booking-add-ons-sidebar__description"
          v-html="addOn.addOnDescription"
        />
        <div
          v-if="isFeesMessageVisible"
          class="booking-add-ons-sidebar__fees-message"
        >
          {{ modifyReservationAddOnsCancelPopupFeesMessage }}
        </div>
        <div class="booking-add-ons-sidebar__form">
          <component
            :is="addOnVariant"
            ref="input"
            :add-on="addOn"
            :data="value.data"
            @reset-quantity="handleResetQuantity"
            @is-valid="isValid = $event"
            @input="formData = $event"
          />
        </div>
      </div>
    </div>
    <div class="booking-add-ons-sidebar__footer">
      <div
        class="booking-add-ons-sidebar__footer-wrapper"
        :class="{
          '--no-selector': (!showAdultSelector && !showChildSelector && !showQuantitySelector),
          '--only-one-selector': showOnlyOneSelector
        }"
      >
        <BookingAddOnsQuantitySelector
          v-if="showQuantitySelector"
          v-model="selectedQuantity"
          :class="['--quantity-selector',
                   {
                     '--only-one-selector': showOnlyOneSelector
                   }]"
          :title="quantitySelectorTitle"
          :max-quantity="maxQuantity"
          :min-quantity="1"
          :price-and-type-label="priceAndTypeLabel"
        />
        <BookingAddOnsQuantitySelector
          v-if="showAdultSelector"
          v-model="selectedAdultGuestQuantity"
          :class="['--adult-selector',
                   {
                     '--only-one-selector': showOnlyOneSelector
                   }]"
          :title="adultSelectorTitle"
          :max-quantity="maxAdultQuantity"
          :min-quantity="minAdultQuantity"
          :price-and-type-label="priceAndTypeLabel"
        />
        <BookingAddOnsQuantitySelector
          v-if="showChildSelector"
          v-model="selectedChildGuestQuantity"
          class="--children-selector"
          :title="childrenSelectorTitle"
          :max-quantity="maxChildQuantity"
          :min-quantity="minChildQuantity"
          :show-tooltip="addOn.excludeKidsFromPriceCalculations"
          :tooltip-message="addOnsComponentData.TooltipChildrenNotIncluded"
        />
        <div class="booking-add-ons-sidebar__footer__total">
          <span class="booking-add-ons-sidebar__footer__total-label">
            {{ tc('booking.total') }}
          </span>
          <span class="booking-add-ons-sidebar__footer__total-value">
            {{ totalPriceFormatted }}
          </span>
        </div>
        <button
          class="booking-add-ons-sidebar__footer__add-cart-cta"
          :disabled="!isValid"
          :class="[{ loading: cartPending }]"
          @click="updateValue"
        >
          {{ addToCartLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import TabTrapMixin from '@mixins/tab-trap'
  import Tracking from '@mixins/tracking'
  import ModalAccessibility from '@mixins/modal-accessibility'
  import BookingAddOnsQuantitySelector from './booking-add-ons-quantity-selector.vue'
  import DateWithoutInterval from './add-ons-variants/date-without-interval.vue'
  import AirportTransferService from './add-ons-variants/airport-transfer-service.vue'

  export default {
    name: 'BookingAddOnsSidebar',
    components: {
      DateWithoutInterval,
      AirportTransferService,
      BookingAddOnsQuantitySelector
    },
    mixins: [TabTrapMixin, ModalAccessibility, Tracking],
    props: {
      value: {
        type: Object,
        required: true
      },
      isEditing: {
        type: Boolean,
        default: false
      },
      outerActivator: HTMLElement,
    },
    data() {
      return {
        isValid: false,
        hasTimeInterval: false,
        hasAvailableDate: false,
        quantitySelectorTitle: '',
        adultSelectorTitle: '',
        childrenSelectorTitle: '',
        selectedQuantity: 1,
        selectedAdultGuestQuantity: 1,
        selectedChildGuestQuantity: 1,
        minAdultQuantity: 1,
        minChildQuantity: 0,
        addOnVariant: null,
        showQuantitySelector: false,
        showAdultSelector: false,
        showChildSelector: false,
        totalPrice: 0.00,
        totalTax: 0.00,
        formEnabled: true,
        formData: {},
        addOnTax: {}
      }
    },
    computed: {
      ...mapState('cart', ['cartPending']),
      ...mapState('booking', ['currentRoomId']),
      ...mapState('auth', ['addOnsComponentData']),
      ...mapState('modifyReservation', ['isBookingEditMode']),
      ...mapState('modifyReservation', ['modifyBookingPropData']),
      ...mapGetters('cart', ['getValidAddOnsByRoomId']),
      ...mapGetters('currency', ['getDisplayPrice']),
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('booking', ['currentRoomGuests', 'getQuery', 'bookingFlow']),
      ...mapGetters('booking', ['showPricesWithTaxes']),

      /**
       * Config for TabTrapMixin
      */
      noInitialFocus() {
        return false
      },

      /**
       * Computed property to get the add-on based on the current room ID and sitecore ID
      */
      addOn() {
        const addOn = this.getValidAddOnsByRoomId(this.currentRoomId).find((e) => this.value.sitecoreId === e.sitecoreId)
        return { ...addOn, synXisId: this.value.synXisId }
      },

      /**
       * Computed property to get the display price of the add-on
      */
      addOnPrice() {
        if (this.addOn.airportAddOn) {
          const vehiclePriceWithoutTaxes = Number(this.selectedAirportTransferVehicle?.price[0]?.base?.amountBeforeTaxField)
          const vehiclePriceWithTaxes = Number(this.selectedAirportTransferVehicle?.price[0]?.base?.amountAfterTaxField)
          return this.getDisplayPrice({ price: this.showPricesWithTaxes() ? vehiclePriceWithTaxes : vehiclePriceWithoutTaxes })
        }
        return this.getDisplayPrice({ price: this.showPricesWithTaxes() ? this.addOn.price[0].base.amountAfterTaxField : this.addOn.price[0].base.amountBeforeTaxField })
      },

      /**
       * Computed property to get the label for the service pricing type
      */
      servicePricingTypeLabel() {
        return this.addOn.airportAddOn
          ? false
          : this.addOnsComponentData.ServicePricingType[this.addOn.servicePricingType]
      },

      /**
       * Computed property to get the formatted total price with ISO code
      */
      totalPriceFormatted() {
        const totalTax = this.showPricesWithTaxes() ? 0 : this.totalTax
        return this.getDisplayPrice({ price: this.totalPrice - totalTax, withIsoCode: true, isoAfter: true })
      },

      /**
       * Computed property to get the selected add-on dates that are available
      */
      formSelectedAddOnDates() {
        if (!this.formData.selectedAddOnDates || !this.addOn.availableDates) {
          return []
        }
        return this.formData.selectedAddOnDates.map((selectedDate) => this.addOn.availableDates.find((availableDate) => selectedDate === availableDate.startTime)).filter(Boolean)
      },

      /**
       * Computed property to get the prices of the selected add-on dates before tax
      */
      selectedAddOnDatesPrices() {
        return this.formSelectedAddOnDates.map((date) => date.price[0].base.amountBeforeTaxField)
      },

      /**
       * Computed property to get the prices of the selected add-on dates with tax
      */
      selectedAddOnDatesPricesWithTax() {
        return this.formSelectedAddOnDates.map((date) => date.price[0].base.amountAfterTaxField)
      },

      selectedAirportTransferVehicle() {
        return this.addOn.vehicleDetails.find((vehicle) => vehicle.synxisId === this.formData.vehicleType)
      },

      selectedAirportTransferVehiclePrice() {
        // Determine if the add-on only contains data for the vehicle type
        if (!this.selectedAirportTransferVehicle?.availableOnArrival && !this.selectedAirportTransferVehicle?.availableOnDeparture && this.isValid) {
          return this.selectedAirportTransferVehicle?.price[0]
        }
        const selectedDate = this.selectedAirportTransferVehicle?.availableOnArrival ? this.formData?.arrivalDate : this.formData?.departureDate
        const vehiclePrice = this.selectedAirportTransferVehicle?.price.find((price) => {
          const priceDateWithoutTimezone = new Date(price.effectiveDate).toISOString().split('T')[0]
          const selectedDateWithoutTimezone = new Date(selectedDate).toISOString().split('T')[0]
          return selectedDateWithoutTimezone === priceDateWithoutTimezone
        })
        return vehiclePrice
      },
      /**
       * Computed property to get the prices of the selected aiport transfer addon before tax
      */
      selectedAirportTransferPrices() {
        return Number(this.selectedAirportTransferVehiclePrice?.base?.amountBeforeTaxField)
      },

      /**
       * Computed property to get the prices of the selected  aiport transfer addon with tax
       */
      selectedAirportTransferPricesWithTax() {
        return Number(this.selectedAirportTransferVehiclePrice?.base?.amountAfterTaxField)
      },

      /**
       * Computed property to get the adult guest count in the current room
      */
      currentRoomAdultGuestCount() {
        return Number(this.currentRoomGuests.adultGuestCount)
      },

      /**
       * Computed property to get the child guest count in the current room
      */
      currentRoomChildGuestCount() {
        return Number(this.currentRoomGuests.childGuestCount)
      },

      /**
       * Computed property to get the combined price and type label
      */
      priceAndTypeLabel() {
        return this.servicePricingTypeLabel ? `${this.addOnPrice} / ${this.servicePricingTypeLabel}` : this.addOnPrice
      },

      maxQuantity() {
        if (this.addOn.airportAddOn) {
          return this.selectedAirportTransferVehicle ? Number(this.selectedAirportTransferVehicle?.quantity) : this.currentRoomAdultGuestCount
        }
      },
      maxAdultQuantity() {
        return this.currentRoomAdultGuestCount
      },
      maxChildQuantity() {
        return this.currentRoomChildGuestCount
      },
      /**
       * Computed property to get the total of guest selected
      */
      totalSelectedQuantity() {
        if (this.addOn.airportAddOn || this.addOn.servicePricingType === 'per-stay' || this.addOn.servicePricingType === 'per-night') {
          return this.selectedQuantity
        }
        return this.addOn.excludeKidsFromPriceCalculations
          ? this.selectedAdultGuestQuantity
          : this.selectedAdultGuestQuantity + this.selectedChildGuestQuantity
      },

      addToCartLabel() {
        return this.addOnsComponentData?.AddToCartLabel || ''
      },
      showOnlyOneSelector() {
        return (this.showQuantitySelector && !this.showAdultSelector && !this.showChildSelector)
          || (!this.showQuantitySelector && this.showAdultSelector && !this.showChildSelector)
      },
      modifyReservationAddOnsCancelPopupFeesMessage() {
        return this.modifyBookingPropData?.ModifyReservationAddOnsCancelPopupFeesMessage
          ? this.modifyBookingPropData.ModifyReservationAddOnsCancelPopupFeesMessage
          : ''
      },
      isFeesMessageVisible() {
        return this.addOn.showPopUpForRemovingAddOnSbe
      },
    },
    watch: {
      formData: {
        handler() {
          this.calculateTotalPrice()
        },
        deep: true,
      },
      totalSelectedQuantity: {
        handler() {
          this.calculateTotalPrice()
        },
      },
    },
    created() {
      this.init()
    },
    mounted() {
      this.setPageContentAccessibility(true)
      this.$track(this.getTrackingData(this.isBookingEditMode ? 'modifyReservation' : 'booking', this.getQuery, {
        event: 'bookingStateChange',
        bookingEngine: {
          bookingStepNumber: '3.6',
          bookingStepName: 'Add Ons Selector',
          bookingEngineVersion: this.isBookingEditMode ? '2' : '1',
          bookingFlow: this.bookingFlow()
        }
      }))
      
      if (window.adobe?.target?.triggerView) {
        window.adobe.target.triggerView('Add Ons Selector')
      }
    },
    methods: {
      init() {
        document.querySelector('body').style.overflowY = 'hidden'
        this.initData()
        this.configAddOnVariant()
        this.calculateTotalPrice()
      },
      initData() {
        if (!this.value.data) {
          this.$emit('input', {
            ...this.value,
            data: {}
          })
        }
      },
      updateValue() {
        const updatedValue = {
          ...this.value,
          sitecoreId: this.addOn.sitecoreId,
          synXisId: this.addOn.synXisId,
          code: this.addOn.synXisId,
          addOnName: this.addOn.addOnName,
          servicePricingType: this.addOn.servicePricingType,
          total: this.totalPrice,
          totalTax: this.totalTax,
          addOnTax: this.addOnTax,
          adultQuantity: this.selectedAdultGuestQuantity,
          childQuantity: this.selectedChildGuestQuantity,
          data: this.formData ? {
            ...this.formData
          } : {}
        }
        if (this.addOnVariant === 'date-without-interval') {
          updatedValue.price = this.selectedAddOnDatesPrices
          updatedValue.priceWithTaxes = this.selectedAddOnDatesPricesWithTax
        } else if (this.addOnVariant === 'airport-transfer-service') {
          updatedValue.servicePricingType = this.selectedAirportTransferVehicle.servicePricingType
          updatedValue.price = [this.selectedAirportTransferPrices]
          updatedValue.priceWithTaxes = [this.selectedAirportTransferPricesWithTax]
          updatedValue.synXisId = this.formData.vehicleType
          updatedValue.code = this.formData.vehicleType
          updatedValue.quantity = this.selectedQuantity
          updatedValue.data.carQuantity = this.selectedQuantity
          delete updatedValue.adultQuantity
          delete updatedValue.childQuantity
        } else {
          updatedValue.price = [this.addOn.price[0].base.amountBeforeTaxField]
          updatedValue.priceWithTaxes = [this.addOn.price[0].base.amountAfterTaxField]

          if (this.addOn.servicePricingType === 'per-stay' || this.addOn.servicePricingType === 'per-night') {
            updatedValue.quantity = this.selectedQuantity
            delete updatedValue.adultQuantity
            delete updatedValue.childQuantity
          }
        }

        this.$emit('input', updatedValue)
        this.$emit('done')
      },
      deactivate() {
        this.setPageContentAccessibility(false)
        document.querySelector('body').style.overflowY = 'auto'
        this.$emit('deactivate')
      },
      closeModal() {
        this.deactivate()
      },
      configAddOnVariant() {
        this.adultSelectorTitle = this.addOnsComponentData?.AdultLabel || ''
        this.childrenSelectorTitle = this.addOnsComponentData?.ChildLabel || ''

        this.selectedQuantity = this.isEditing && this.value.quantity ? Number(this.value.quantity) : 1
        this.selectedAdultGuestQuantity = this.isEditing && this.value.adultQuantity ? Number(this.value.adultQuantity) : this.minAdultQuantity
        this.selectedChildGuestQuantity = this.isEditing && this.value.childQuantity ? Number(this.value.childQuantity) : this.minChildQuantity

        this.hasAvailableDate = this.addOn.availableDates?.length > 0

        /**
        * Determine the add-on variant based on servicePricingType
        */
        try {
          if (this.addOn.airportAddOn) {
            this.handleAirportTransfer()
          } else {
            switch (this.addOn.servicePricingType) {
            case 'per-person-per-night':
              this.handlePerPersonPerNight()
              break

            case 'per-person':
              this.handlePerPerson()
              break

            case 'per-night':
              this.handlePerNight()
              break

            case 'per-stay':
              this.handlePerStay()
              break

            default:
              throw new Error(`No variant found for servicePricingType: ${this.addOn.servicePricingType}`)
            }
          }
        } catch (error) {
          console.error(error.message)
          this.addOnVariant = null
          this.showAdultSelector = false
          this.showChildSelector = false
          this.isValid = false
        }
      },

      handlePerPersonPerNight() {
        this.showAdultSelector = true
        this.showChildSelector = this.currentRoomChildGuestCount > 0
        if (this.hasAvailableDate && !this.hasTimeInterval) {
          this.addOnVariant = 'date-without-interval'
        }
      },
      handlePerPerson() {
        this.showAdultSelector = true
        this.showChildSelector = this.currentRoomChildGuestCount > 0
        if (this.hasAvailableDate && !this.hasTimeInterval) {
          this.addOnVariant = 'date-without-interval'
        } else {
          this.isValid = true
        }
      },
      handlePerNight() {
        this.isValid = true
      },
      handlePerStay() {
        this.selectedQuantity = this.isEditing ? Number(this.value.quantity) : 1
        this.isValid = true
      },
      handleAirportTransfer() {
        this.quantitySelectorTitle = this.addOn?.footerCounterLabel || ''
        this.showQuantitySelector = true
        this.showAdultSelector = false
        this.showChildSelector = false
        this.addOnVariant = 'airport-transfer-service'
      },

      calculateTotalPrice() {
        try {
          if (this.addOnVariant === 'date-without-interval') {
            this.calculateDateWithoutIntervalPrice()
          } else if (this.addOnVariant === 'airport-transfer-service') {
            this.calculateAirportTransferPrice()
          } else if (this.addOnVariant === null) {
            this.calculateBasePrice()
          } else {
            throw new Error(`Error while calculating total for: ${this.addOn.servicePricingType}`)
          }
        } catch (error) {
          console.error(error)
          this.totalPrice = '0.00'
          this.totalTax = '0.00'
        }
      },

      calculateDateWithoutIntervalPrice() {
        const { servicePricingType } = this.addOn || {}

        if ((servicePricingType === 'per-person-per-night' || servicePricingType === 'per-person') && this.formSelectedAddOnDates.length) {
          const selectedDatesTotal = this.selectedAddOnDatesPricesWithTax.reduce((a, b) => Number(a) + Number(b), 0)
          const selectedDatesTaxesTotal = this.selectedAddOnDatesPricesWithTax.reduce((acc, priceWithTax, index) => {
            const tax = Number(priceWithTax) - Number(this.selectedAddOnDatesPrices[index])
            return acc + tax
          }, 0)

          this.totalPrice = (selectedDatesTotal * this.totalSelectedQuantity).toFixed(2)
          this.totalTax = (selectedDatesTaxesTotal * this.totalSelectedQuantity).toFixed(2)

          this.addOnTax = this.formSelectedAddOnDates.reduce((acc, date) => {
            acc[date.price[0].effectiveDate] = [...date.price[0].base.taxes]
            return acc
          }, {})
        } else {
          this.totalPrice = '0.00'
          this.totalTax = '0.00'
          this.addOnTax = {}
        }
      },

      calculateAirportTransferPrice() {
        if (this.selectedAirportTransferVehicle && this.selectedAirportTransferVehiclePrice) {
          const price = Number(this.selectedAirportTransferPrices)
          const priceWithTaxes = Number(this.selectedAirportTransferPricesWithTax)
          const taxValue = priceWithTaxes - price

          this.totalPrice = (priceWithTaxes * this.totalSelectedQuantity).toFixed(2)
          this.totalTax = (taxValue * this.totalSelectedQuantity).toFixed(2)
          this.addOnTax = {
            [this.selectedAirportTransferVehiclePrice.effectiveDate]: [...this.selectedAirportTransferVehiclePrice.base.taxes]
          }
        } else {
          this.totalPrice = '0.00'
          this.totalTax = '0.00'
          this.addOnTax = {}
        }
      },

      calculateBasePrice() {
        const { price } = this.addOn || {}
        const { effectiveDate } = price[0]
        const basePrice = price[0].base || {}

        const amountAfterTax = Number(basePrice.amountAfterTaxField) || 0
        const amountBeforeTax = Number(basePrice.amountBeforeTaxField) || 0

        this.totalPrice = (amountAfterTax * this.totalSelectedQuantity).toFixed(2)
        this.totalTax = ((amountAfterTax - amountBeforeTax) * this.totalSelectedQuantity).toFixed(2)
        this.addOnTax = {
          [effectiveDate]: [...basePrice.taxes]
        }
      },

      handleResetQuantity() {
        this.selectedQuantity = 1
        this.selectedAdultGuestQuantity = this.minAdultQuantity
        this.selectedChildGuestQuantity = this.minChildQuantity
      }
    }
  }
</script>
